import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, List, ListItem,
  ListItemText, TextField, Typography, makeStyles,
} from '@material-ui/core'
import api from '../../api/client'
import { toast } from '../../api/toast'

/**
 * فاز ۱ — کارت «معرفی ناظر/مشاور» در پنل پیمانکار.
 *
 * نیمه‌اجباری و بدون انسداد: اگر قراردادی ناظر/مشاور نداشته باشد، این کارت
 * با شمارش نشان داده می‌شود و پیمانکار می‌تواند معرفی کند یا بعداً انجام دهد.
 */
const useStyles = makeStyles(() => ({
  card: {
    marginBottom: 16,
    borderRight: '5px solid #f9a825',
    background: '#fffdf3',
  },
  title: { fontWeight: 800, fontSize: 15, color: '#6d4c00' },
  sub: { fontSize: 12.5, color: '#8a6d00', marginTop: 6, lineHeight: 1.9 },
  chip: { marginInlineStart: 6, fontWeight: 700 },
  row: {
    padding: '10px 0',
    borderBottom: '1px solid #ececec',
  },
  rowTitle: { fontWeight: 700, fontSize: 13 },
  rowSub: { fontSize: 11.5, color: '#777', marginTop: 3 },
  form: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: 8,
  },
  list: { maxHeight: '54vh', overflow: 'auto', paddingInlineEnd: 4 },
  pick: {
    marginTop: 8,
    maxHeight: 150,
    overflow: 'auto',
    border: '1px solid #eee',
    borderRadius: 8,
  },
}))

const unwrap = (r) => (r && r.data !== undefined ? r.data : r)

const IntroduceAssignmentCard = () => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  // فرم معرفی ناظر برای یک قرارداد
  const [supFor, setSupFor] = useState(null)
  const [supName, setSupName] = useState('')
  const [supMobile, setSupMobile] = useState('')
  // جست‌وجوی ناظر در «فهرست اشخاص» (برگرفته از ارجان فیش)
  const [perQuery, setPerQuery] = useState('')
  const [perResults, setPerResults] = useState([])
  const [perBusy, setPerBusy] = useState(false)
  const perDeb = useRef(null)

  // انتخاب مشاور برای یک قرارداد
  const [conFor, setConFor] = useState(null)
  const [conQuery, setConQuery] = useState('')
  const [conResults, setConResults] = useState([])
  const [conBusy, setConBusy] = useState(false)
  const deb = useRef(null)

  const load = useCallback(async () => {
    try {
      const r = await api.get('contracts/assignments/pending/')
      setData(unwrap(r))
    } catch (e) {
      // اگر کاربر پیمانکار نباشد یا خطایی رخ دهد، کارت نمایش داده نمی‌شود
      setData(null)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const need = data
    ? (Number(data.need_supervisor || 0) + Number(data.need_consultant || 0))
    : 0

  const resetForms = () => {
    setSupFor(null); setSupName(''); setSupMobile('')
    setPerQuery(''); setPerResults([])
    setConFor(null); setConQuery(''); setConResults([])
  }

  const submitSupervisor = async () => {
    if (!supFor) return
    if (String(supName).trim().length < 3) {
      toast('نام و نام خانوادگی ناظر را کامل وارد کنید', 'error'); return
    }
    setBusy(true)
    try {
      await api.post('contracts/assignments/supervisor/', {
        contract_id: supFor.contract_id,
        name: String(supName).trim(),
        mobile: String(supMobile).trim(),
      })
      toast('ناظر ثبت شد', 'success')
      resetForms()
      await load()
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.error) || 'ثبت ناظر انجام نشد'
      toast(msg, 'error')
    } finally {
      setBusy(false)
    }
  }

  const searchContractors = (q) => {
    setConQuery(q)
    if (deb.current) clearTimeout(deb.current)
    deb.current = setTimeout(async () => {
      setConBusy(true)
      try {
        const r = await api.get('contracts/assignments/contractors/?q=' + encodeURIComponent(q || ''))
        setConResults(unwrap(r) || [])
      } catch (e) {
        setConResults([])
      } finally {
        setConBusy(false)
      }
    }, 300)
  }

  // جست‌وجوی ناظر در فهرست اشخاص (نام/موبایل) → پر شدن خودکار نام و موبایل
  const searchPeople = (q) => {
    setPerQuery(q)
    if (perDeb.current) clearTimeout(perDeb.current)
    if (!q || q.trim().length < 3) { setPerResults([]); return }
    perDeb.current = setTimeout(async () => {
      setPerBusy(true)
      try {
        const r = await api.get('contracts/assignments/people/?q=' + encodeURIComponent(q.trim()))
        setPerResults(unwrap(r) || [])
      } catch (e) {
        setPerResults([])
      } finally {
        setPerBusy(false)
      }
    }, 300)
  }

  const pickPerson = (p) => {
    setSupName(p.name || '')
    setSupMobile(p.mobile || '')
    setPerResults([])
    setPerQuery('')
  }

  const submitConsultant = async (c) => {
    if (!conFor || !c) return
    setBusy(true)
    try {
      await api.post('contracts/assignments/consultant/', {
        contract_id: conFor.contract_id,
        consultant_id: c.id,
      })
      toast('مشاور ثبت شد', 'success')
      resetForms()
      await load()
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.error) || 'ثبت مشاور انجام نشد'
      toast(msg, 'error')
    } finally {
      setBusy(false)
    }
  }

  if (!data || need <= 0) return null

  const items = data.items || []

  return (
    <>
      <Card className={classes.card} elevation={1} data-tour="introduce-assignment">
        <CardContent>
          <Typography className={classes.title}>
            🧑‍💼 معرفی ناظر و مشاور
          </Typography>
          <Typography className={classes.sub}>
            برای شفافیت و ارتباط بهتر، ناظر و مشاور قراردادهای خود را معرفی کنید.
            <br />
            <Chip size="small" className={classes.chip} style={{ background: '#ffe9a8' }}
              label={`بدون ناظر: ${data.need_supervisor}`} />
            <Chip size="small" className={classes.chip} style={{ background: '#d9ecff' }}
              label={`بدون مشاور: ${data.need_consultant}`} />
          </Typography>
          <Box mt={1.5}>
            <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
              شروع معرفی
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => { setOpen(false); resetForms() }} fullWidth maxWidth="md" dir="rtl">
        <DialogTitle style={{ fontWeight: 800 }}>
          معرفی ناظر و مشاور قراردادها
        </DialogTitle>
        <DialogContent>
          {items.length === 0 && (
            <Typography style={{ padding: 12 }}>موردی برای معرفی نیست ✓</Typography>
          )}
          <div className={classes.list}>
            {items.map((it) => (
              <div key={it.contract_id} className={classes.row}>
                <Typography className={classes.rowTitle}>
                  قرارداد {it.number} — {it.organization}
                </Typography>
                <Typography className={classes.rowSub}>
                  {it.subject || 'بدون موضوع'}
                </Typography>

                <Box mt={1} display="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {it.need_supervisor && (
                    <Button size="small" variant="outlined" color="primary"
                      onClick={() => { setSupFor(it); setSupName(''); setSupMobile('') }}>
                      معرفی ناظر
                    </Button>
                  )}
                  {it.need_consultant && (
                    <Button size="small" variant="outlined"
                      onClick={() => { setConFor(it); searchContractors('') }}>
                      انتخاب مشاور
                    </Button>
                  )}
                  {!it.need_supervisor && (
                    <Chip size="small" color="primary" variant="outlined"
                      label={`ناظر: ${it.supervisor_name || 'ثبت‌شده'}`} />
                  )}
                  {!it.need_consultant && (
                    <Chip size="small" variant="outlined"
                      label={`مشاور: ${it.consultant_name || 'ثبت‌شده'}`} />
                  )}
                </Box>

                {/* فرم معرفی ناظر */}
                {supFor && supFor.contract_id === it.contract_id && (
                  <div>
                    <div className={classes.form}>
                      <TextField size="small" variant="outlined" style={{ minWidth: 280 }}
                        label="جست‌وجوی ناظر در فهرست (نام یا موبایل)"
                        value={perQuery} onChange={(e) => searchPeople(e.target.value)}
                        placeholder="حداقل ۳ حرف" />
                    </div>
                    {perBusy && <Box p={1}><CircularProgress size={16} /></Box>}
                    {!perBusy && perResults.length > 0 && (
                      <div className={classes.pick} style={{ maxHeight: 170 }}>
                        <List dense>
                          {perResults.map((p) => (
                            <ListItem key={p.id} button onClick={() => pickPerson(p)}>
                              <ListItemText
                                primary={p.name}
                                secondary={p.mobile + (p.organization ? ' · ' + p.organization : '')} />
                            </ListItem>
                          ))}
                        </List>
                      </div>
                    )}
                    <div className={classes.form}>
                      <TextField size="small" variant="outlined" label="نام و نام خانوادگی ناظر"
                        value={supName} onChange={(e) => setSupName(e.target.value)} />
                      <TextField size="small" variant="outlined" label="موبایل ناظر"
                        value={supMobile} onChange={(e) => setSupMobile(e.target.value)}
                        placeholder="09123456789" />
                      <Button size="small" variant="contained" color="primary"
                        disabled={busy} onClick={submitSupervisor}>ثبت ناظر</Button>
                      <Button size="small" onClick={() => setSupFor(null)}>انصراف</Button>
                    </div>
                  </div>
                )}

                {/* انتخاب مشاور */}
                {conFor && conFor.contract_id === it.contract_id && (
                  <div style={{ marginTop: 8 }}>
                    <TextField size="small" variant="outlined" fullWidth
                      label="جست‌وجوی مشاور (نام یا شناسهٔ ملی)"
                      value={conQuery} onChange={(e) => searchContractors(e.target.value)} />
                    <div className={classes.pick}>
                      {conBusy && (
                        <Box p={1.5} textAlign="center"><CircularProgress size={18} /></Box>
                      )}
                      {!conBusy && conResults.length === 0 && (
                        <Typography style={{ padding: 10, fontSize: 12, color: '#888' }}>
                          موردی یافت نشد — نام یا شناسهٔ ملی را وارد کنید
                        </Typography>
                      )}
                      <List dense>
                        {conResults.map((c) => (
                          <ListItem key={c.id} button onClick={() => submitConsultant(c)}>
                            <ListItemText
                              primary={c.name || `پیمانکار ${c.id}`}
                              secondary={c.national_code || ''} />
                          </ListItem>
                        ))}
                      </List>
                    </div>
                    <Box mt={0.5}>
                      <Button size="small" onClick={() => setConFor(null)}>انصراف</Button>
                    </Box>
                  </div>
                )}

                <Divider style={{ marginTop: 10 }} />
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); resetForms() }}>بستن</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default IntroduceAssignmentCard
